import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cpu, Plus, Edit, Trash2, Link2, Search, RefreshCw,
  AlertCircle, X, Check, XCircle, Key, Copy
} from 'lucide-react';
import toast from 'react-hot-toast';
import { devicesAPI } from '../../services/api';

const DevicesManagement = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    device_name: '',
    manufacturer: '',
    model: '',
    description: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // API Key state
  const [apiKeyData, setApiKeyData] = useState({ hasApiKey: false, maskedKey: null });
  const [fullApiKey, setFullApiKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  // Dummy data (with numeric id as per device_full.md)
  const dummyDevices = [
    { _id: '1', id: 1, device_name: 'COBAS_E411', manufacturer: 'Roche', model: 'COBAS_E411', active: true },
    { _id: '2', id: 2, device_name: 'COBAS_C311', manufacturer: 'Roche', model: 'COBAS_C311', active: true },
    { _id: '3', id: 3, device_name: 'BS240', manufacturer: 'Mindray', model: 'BS-240', active: true },
    { _id: '4', id: 4, device_name: 'CL900i', manufacturer: 'Mindray', model: 'CL-900i', active: true },
  ];

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await devicesAPI.getAll();
      const data = response.data?.data || response.data || [];
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('حدث خطأ في تحميل البيانات - عرض بيانات تجريبية');
      setDevices(dummyDevices);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // Load API key on mount
  useEffect(() => {
    devicesAPI.getApiKey().then(res => {
      setApiKeyData(res.data?.data || { hasApiKey: false, maskedKey: null });
    }).catch(() => {});
  }, []);

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      const res = await devicesAPI.generateApiKey();
      const key = res.data?.data?.deviceApiKey;
      setFullApiKey(key);
      setApiKeyData({ hasApiKey: true, maskedKey: key.slice(0, 3) + '\u2022'.repeat(key.length - 11) + key.slice(-8) });
      setShowRegenerateConfirm(false);
      toast.success('\u062A\u0645 \u062A\u0648\u0644\u064A\u062F \u0627\u0644\u0645\u0641\u062A\u0627\u062D \u0628\u0646\u062C\u0627\u062D');
    } catch (err) {
      toast.error('\u0641\u0634\u0644 \u062A\u0648\u0644\u064A\u062F \u0627\u0644\u0645\u0641\u062A\u0627\u062D');
    }
    setGeneratingKey(false);
  };

  const handleCopyKey = () => {
    if (fullApiKey) {
      navigator.clipboard.writeText(fullApiKey);
      toast.success('\u062A\u0645 \u0646\u0633\u062E \u0627\u0644\u0645\u0641\u062A\u0627\u062D');
    }
  };

  const resetForm = () => {
    setFormData({ device_name: '', manufacturer: '', model: '', description: '', active: true });
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (device) => {
    setSelectedDevice(device);
    setFormData({
      device_name: device.device_name || '',
      manufacturer: device.manufacturer || '',
      model: device.model || '',
      description: device.description || '',
      active: device.active !== false
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    if (!formData.device_name.trim()) {
      setError('اسم الجهاز مطلوب');
      return;
    }
    setSaving(true);
    try {
      if (showEditModal && selectedDevice) {
        await devicesAPI.update(selectedDevice._id, formData);
      } else {
        await devicesAPI.create(formData);
      }
      fetchDevices();
      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedDevice) return;
    setDeleting(true);
    try {
      await devicesAPI.delete(selectedDevice._id);
      setDevices(prev => prev.filter(d => d._id !== selectedDevice._id));
      setShowDeleteModal(false);
      setSelectedDevice(null);
    } catch (err) {
      setError('حدث خطأ أثناء الحذف');
    }
    setDeleting(false);
  };

  const filteredDevices = devices.filter(d => 
    d.device_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.model?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputClasses = "w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
            <Cpu className="w-6 h-6 text-slate-100" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إدارة أجهزة المختبر</h1>
            <p className="text-slate-500 text-sm mt-1">Analyzer Devices Management</p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAdd}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          <span>إضافة جهاز جديد</span>
        </motion.button>
      </motion.div>

      {/* API Key Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
              <Key className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">مفتاح ربط الأجهزة (API Key)</h3>
              <p className="text-xs text-gray-500">يُستخدم لربط برنامج LISA Middleware بنظامك</p>
            </div>
          </div>
        </div>

        {fullApiKey ? (
          // Just generated — show full key with copy
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs text-amber-700 font-semibold mb-2">احفظ هذا المفتاح الآن — لن يظهر مرة أخرى</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-amber-200 rounded-lg px-4 py-2.5 text-sm font-mono text-gray-800 select-all" dir="ltr">
                {fullApiKey}
              </code>
              <button onClick={handleCopyKey} className="px-4 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2">
                <Copy className="w-4 h-4" />
                نسخ
              </button>
            </div>
          </div>
        ) : apiKeyData.hasApiKey ? (
          // Has key — show masked
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-mono text-gray-500" dir="ltr">
              {apiKeyData.maskedKey}
            </code>
            <button
              onClick={() => setShowRegenerateConfirm(true)}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              إعادة توليد
            </button>
          </div>
        ) : (
          // No key — generate button
          <button
            onClick={handleGenerateKey}
            disabled={generatingKey}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Key className="w-4 h-4" />
            {generatingKey ? 'جاري التوليد...' : 'توليد مفتاح جديد'}
          </button>
        )}

        {/* Regenerate confirmation */}
        {showRegenerateConfirm && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-sm text-red-700 mb-3">
              سيتم إلغاء المفتاح الحالي فوراً. أي برنامج LISA يستخدم المفتاح القديم سيتوقف عن العمل.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={handleGenerateKey} disabled={generatingKey}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {generatingKey ? 'جاري التوليد...' : 'تأكيد إعادة التوليد'}
              </button>
              <button onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-50">
                إلغاء
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="البحث بالاسم أو الشركة المصنعة..."
              className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
            />
            <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
          <button onClick={fetchDevices} className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3 text-amber-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="mr-auto"><X className="w-4 h-4" /></button>
        </motion.div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Cpu className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">لا يوجد أجهزة</p>
            <button onClick={handleAdd} className="px-5 py-2.5 bg-blue-600 text-white rounded-xl">إضافة جهاز</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">اسم الجهاز</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الشركة المصنعة</th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الموديل</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map((device, index) => (
                  <motion.tr
                    key={device._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150 group"
                  >
                    <td className="px-6 py-5">
                      <span className="font-mono font-bold text-slate-900 text-sm">{device.id || '-'}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                          <Cpu className="w-4 h-4 text-slate-600" strokeWidth={2} />
                        </div>
                        <span className="font-semibold text-slate-900 text-sm">{device.device_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{device.manufacturer || '-'}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-slate-700">{device.model || '-'}</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide border ${
                        device.active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${device.active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {device.active ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => {/* Navigate to mappings */}}
                          className="px-3 py-1.5 bg-white border border-[#1E293B]/20 text-[#1E293B] rounded-lg text-sm hover:bg-slate-50 transition-all duration-150 flex items-center gap-1"
                        >
                          <Link2 className="w-3.5 h-3.5 text-blue-600" strokeWidth={2} />
                          <span>إدارة الربط</span>
                        </button>
                        <button
                          onClick={() => handleEdit(device)}
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all duration-150"
                          title="تعديل"
                        >
                          <Edit className="w-4 h-4" strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setSelectedDevice(device); setShowDeleteModal(true); }}
                          className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-150"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {(showAddModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-[#1E293B] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                  <Cpu className="w-5 h-5" />
                  {showEditModal ? 'تعديل الجهاز' : 'إضافة جهاز جديد'}
                </h3>
                <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="text-slate-400 hover:text-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم الجهاز <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.device_name}
                    onChange={e => setFormData({...formData, device_name: e.target.value})}
                    className={inputClasses}
                    placeholder="مثال: COBAS_E411"
                    dir="ltr"
                  />
                  <p className="text-xs text-gray-400 mt-1">استخدم أحرف كبيرة وشرطات سفلية (UPPERCASE_WITH_UNDERSCORES)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الشركة المصنعة</label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={e => setFormData({...formData, manufacturer: e.target.value})}
                    className={inputClasses}
                    placeholder="مثال: Roche, Siemens, Abbott"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الموديل</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={e => setFormData({...formData, model: e.target.value})}
                    className={inputClasses}
                    placeholder="مثال: COBAS e801, Architect i2000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الوصف (اختياري)</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    className={inputClasses}
                    rows={2}
                    placeholder="ملاحظات أو وصف إضافي للجهاز"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={e => setFormData({...formData, active: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">الجهاز نشط</span>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحفظ...</>
                  ) : (
                    showEditModal ? 'حفظ التغييرات' : 'إضافة الجهاز'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">حذف الجهاز</h3>
                <p className="text-gray-500 mb-6">
                  هل أنت متأكد من حذف الجهاز "{selectedDevice?.device_name}"؟
                  <br />
                  <span className="text-sm text-red-500">سيتم حذف جميع الربط المرتبط بهذا الجهاز</span>
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">إلغاء</button>
                  <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />جاري الحذف...</> : 'حذف'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default DevicesManagement;

